import { Link } from 'react-router';
import { ExternalLink } from 'lucide-react';
import {
  useOverseasPoliciesQuery,
} from '@/features/overseasShipping/hooks/useOverseasShipping';
import {
  OVERSEAS_STORE_FLAGS,
  OVERSEAS_STORE_LABELS,
  OVERSEAS_STORE_SCOPES,
  SHIPPING_BASIS_LABELS,
  type OverseasShippingPolicy,
  type OverseasStoreScope,
} from '@/features/overseasShipping/types/overseasShipping';

/**
 * 항목2 — 상품 폼 §7 배송 섹션의 "해외배송" 표시 전용 블록.
 *
 * 해외배송은 상품별 설정이 아니라 **몰(영문/중문/일문)별 글로벌 정책**이다
 * (`/products/overseas-shipping`). catalog 상품 스키마에는 해외몰 노출/요율 오버라이드
 * 필드가 없으므로(진단 결과 부재 확정), 이 블록은 **선택/저장 없이** 활성 정책의 구성
 * 여부만 요약하고 통합관리 페이지로 안내한다. 내부식별자(policyId)는 노출하지 않는다.
 */

/**
 * 해당 몰 정책이 "구성됨"인지 판정 — 정책관리 화면(OverseasShippingSection)의 활성 기준과
 * 일치시킨다. 정책관리는 `active` 와 권역 그룹 수만으로 정책을 노출하므로, 요약도 동일하게
 * 활성 + 권역 1개 이상이면 구성된 것으로 본다.
 *
 * ★EMS 표준 basis 는 요율표를 수기 입력하지 않고 우체국 표준요율을 따른다
 * (CountryGroupEditor 가 SELF 요율 입력을 숨김). 따라서 EMS 는 요율 행이 0건이어도 정상
 * 구성이며, 자체 요율(SELF) 일 때만 요율 행을 구성 조건으로 요구한다.
 */
const isConfigured = (policy: OverseasShippingPolicy | undefined): boolean => {
  if (!policy || !policy.active) return false;
  const groups = policy.countryGroups ?? [];
  if (groups.length === 0) return false;
  if (policy.basis === 'EMS') return true;
  return groups.some((g) => (g.rates ?? []).length > 0);
};

/**
 * 몰별 구성 요약 문구. 배송기준(자체 요율 / EMS 표준)을 앞세우고 권역 수를 덧붙인다.
 * EMS 는 요율을 수기 입력하지 않으므로 요율 건수 대신 표준요율 적용임을 표기한다.
 * 내부 식별자는 노출하지 않는다.
 */
const summaryText = (policy: OverseasShippingPolicy): string => {
  const groups = policy.countryGroups ?? [];
  const groupCount = groups.length;
  const basisLabel = SHIPPING_BASIS_LABELS[policy.basis];
  if (policy.basis === 'EMS') {
    return `${basisLabel} · 권역 ${groupCount}개`;
  }
  const rateCount = groups.reduce((sum, g) => sum + (g.rates ?? []).length, 0);
  return `${basisLabel} · 권역 ${groupCount}개 · 요율 ${rateCount}건`;
};

interface MallRowProps {
  scope: OverseasStoreScope;
  policy: OverseasShippingPolicy | undefined;
}

const MallRow = ({ scope, policy }: MallRowProps) => {
  const configured = isConfigured(policy);
  return (
    <li
      className="flex items-center justify-between gap-3 rounded-md px-3 py-2"
      style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-soft)' }}
      data-testid={`overseas-mall-row-${scope}`}
    >
      <span className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--content-strong)' }}>
        <span aria-hidden="true">{OVERSEAS_STORE_FLAGS[scope]}</span>
        {OVERSEAS_STORE_LABELS[scope]}몰
      </span>
      {configured && policy ? (
        <span
          className="rounded-full px-2 py-0.5 text-[12px]"
          style={{ background: 'var(--surface-1)', color: 'var(--content-default)' }}
        >
          {summaryText(policy)}
        </span>
      ) : (
        <span
          className="rounded-full px-2 py-0.5 text-[12px] font-medium"
          style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
        >
          미설정
        </span>
      )}
    </li>
  );
};

export const OverseasShippingSummary = () => {
  const { data, isLoading, isError } = useOverseasPoliciesQuery();

  const byScope = new Map<OverseasStoreScope, OverseasShippingPolicy>();
  for (const policy of data ?? []) {
    const existing = byScope.get(policy.storeScope);
    if (!existing || (policy.active && !existing.active)) {
      byScope.set(policy.storeScope, policy);
    }
  }

  return (
    <div
      className="flex flex-col gap-3 rounded-lg p-4"
      style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-soft)' }}
      data-testid="overseas-shipping-summary"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-[14px] font-semibold" style={{ color: 'var(--content-strong)' }}>
            해외배송 (영문 · 중문 · 일문몰)
          </span>
          <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
            해외배송 요율은 몰별 공통 정책으로 관리됩니다. 상품별 개별 설정은 없습니다.
          </span>
        </div>
        <Link
          to="/products/overseas-shipping"
          className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium"
          style={{ color: 'var(--cta-primary)' }}
        >
          통합관리
          <ExternalLink size={13} aria-hidden="true" />
        </Link>
      </div>

      {isLoading ? (
        <p className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
          해외배송 정책을 불러오는 중…
        </p>
      ) : isError ? (
        <p className="text-[13px]" style={{ color: 'var(--signal-danger)' }}>
          해외배송 정책을 불러오지 못했습니다. 통합관리에서 확인하세요.
        </p>
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {OVERSEAS_STORE_SCOPES.map((scope) => (
              <MallRow key={scope} scope={scope} policy={byScope.get(scope)} />
            ))}
          </ul>
          <dl
            className="flex flex-col gap-1 rounded-md px-3 py-2 text-[11px]"
            style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}
            data-testid="overseas-summary-legend"
          >
            <div className="flex gap-1.5">
              <dt className="shrink-0 font-medium" style={{ color: 'var(--content-default)' }}>
                배송기준
              </dt>
              <dd>자체 요율=관리자가 무게 구간별 금액을 직접 입력 / EMS 표준=우체국 EMS 공식 요율표 자동 적용(요율 입력 불필요)</dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="shrink-0 font-medium" style={{ color: 'var(--content-default)' }}>
                권역
              </dt>
              <dd>배송 국가를 묶은 그룹(예: 미주·아시아). 그룹마다 요율을 따로 둡니다.</dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="shrink-0 font-medium" style={{ color: 'var(--content-default)' }}>
                요율
              </dt>
              <dd>무게 구간별 배송 금액(자체 요율에서만 입력). EMS 표준은 공식 요율표를 따릅니다.</dd>
            </div>
          </dl>
        </>
      )}
    </div>
  );
};
